import { useMemo } from 'react';

import type { RetrieveUsersMe200Response } from '@spazious/client-backoffice';
import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';

import { normalizeApiResponse } from '../_normalize.js';
import { useSwallowedMutation } from '../_useSwallowedMutation.js';
import { invalidateMutationKey, joinInclude } from '../_utils.js';
import { useApiClient } from '../useApiClient.js';

export const ME_QUERY_KEY = 'me';

// Canonical include for the full-user `me` read. Consumers passing it share ONE
// cache entry, so the prefs dialog reuses QuickUserToggler's warm cache.
// `profile.hotel` is the hotel a hotel admin owns. It is NOT the header hotel
// selector, which only mirrors the URL and is empty until the user picks one —
// so anything that must name the acting user's own hotel reads it from here.
export const ME_INCLUDE = ['profile.hotel', 'profile.hotelChain', 'usersGridConfig'];

// Serializes every `me` config write across hook instances. The config endpoint replaces the WHOLE
// blob, so concurrent full-blob writers (the first-day dialog, row-order drags) must never derive
// from the same stale blob — each queued write re-reads the latest cached blob at execution time,
// so a later writer preserves an earlier writer's just-saved keys instead of clobbering them.
let configWriteQueue: Promise<unknown> = Promise.resolve();

export type UpdateMyConfigVariables = {
  userId: string;
  // Applied to the LATEST persisted config blob at execution time (not render time), so a concurrent
  // writer's just-saved keys survive. Must be a pure read-modify-write of the opaque JSON string.
  updateConfig: (currentConfig: string | undefined) => string;
};

// `UsersApiRetrieveUsersMeRequest` also exposes `sort` and `filterSearch` —
// codegen artifacts inherited from the list endpoint family. They have no
// semantic on `/users/me` (single-record). Intentionally omitted to match
// the legacy `useGetMe` surface.
type UseMeParams = {
  include?: string[];
  fields?: Record<string, string>;
  enabled?: boolean;
  // Extra cache key(s) to invalidate after `updateMyConfig`. The `me` cache is
  // patched in place on success, so a self-edit does not need `[[ME_QUERY_KEY]]`.
  mutationKey?: QueryKey | QueryKey[];
  onUpdateConfig?: () => void;
  onError?: (error: unknown) => void;
};

export const useMe = ({
  include,
  fields,
  enabled = true,
  mutationKey,
  onUpdateConfig,
  onError,
}: UseMeParams = {}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => [ME_QUERY_KEY, include?.join(','), fields], [include, fields]);

  const { data, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.users.retrieveUsersMe({
        include: joinInclude(include),
        fields,
      });
      return response.data;
    },
    enabled,
  });

  const normalizedData = useMemo(() => (data ? normalizeApiResponse(data) : null), [data]);

  // Self-service write of the user's own `config` blob via `PATCH /users/users/{id}/config/`.
  // Envelope requires `type: 'User'` + the user's own `id`. Writes go through `configWriteQueue`
  // (module-level) so overlapping full-blob writers can't clobber each other's keys; each queued
  // write reads the freshest cached blob, applies `updateConfig`, PATCHes, then patches the cache in
  // place — so the next queued write and every reader see the result immediately.
  const { mutateAsync: updateMyConfig, isPending } = useSwallowedMutation({
    mutationFn: ({ userId, updateConfig }: UpdateMyConfigVariables) => {
      const run = configWriteQueue.then(async () => {
        const currentConfig =
          queryClient.getQueryData<RetrieveUsersMe200Response>(queryKey)?.data?.attributes?.config;
        const config = updateConfig(typeof currentConfig === 'string' ? currentConfig : undefined);

        const response = await apiClient.users.updateConfigUsersUsersIdConfig({
          id: userId,
          updateConfigUsersUsersIdConfigRequest: {
            data: { type: 'User', id: userId, attributes: { config } },
          },
        });

        // Prefer the server echo so a backend-normalized blob (reordered/dropped keys) stays
        // authoritative; fall back to the sent value if the response omits it.
        const serverConfig = response.data?.data?.attributes?.config ?? config;
        queryClient.setQueryData<RetrieveUsersMe200Response>(queryKey, (previous) =>
          previous?.data?.attributes
            ? {
                ...previous,
                data: {
                  ...previous.data,
                  attributes: { ...previous.data.attributes, config: serverConfig },
                },
              }
            : previous
        );
        return response.data;
      });
      // Keep the queue alive even if this write fails, so a rejection can't wedge later writes.
      configWriteQueue = run.catch(() => {});
      return run;
    },
    onSuccess: () => {
      onUpdateConfig?.();
      invalidateMutationKey(queryClient, mutationKey);
    },
    onError,
  });

  return useMemo(
    () => ({ data, normalizedData, isFetching, updateMyConfig, isPending }),
    [data, normalizedData, isFetching, updateMyConfig, isPending]
  );
};
